import React, { useCallback } from "react";
import { Printer, Shield, Drama, Gavel } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate } from "@/components/PageHeader";
import { formatDateOnly } from "@/lib/date";
import ConferenceLog, { hasConference } from "@/components/ConferenceLog";
import { MeritCaseRow, caseColumns, CASE_EMPTY } from "@/components/merits/MeritCaseCells";
import SubmissionReviewCard, { useReveal } from "@/components/review/SubmissionReviewCard";
import { fmtPts } from "@/components/thespian/ThespianRankCard";
import ActivityLabel from "@/components/thespian/ActivityLabel";
import { shortNo } from "@/lib/caseNo";

const Pill = ({ children, className = "bg-slate-100 text-slate-600", testId }) => (
  <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${className}`} data-testid={testId}>{children}</span>
);
const Empty = ({ text, testId }) => <p className="rounded-lg border border-dashed border-slate-200 p-4 text-sm text-slate-400" data-testid={testId}>{text}</p>;
const SectionTitle = ({ children }) => <h4 className="text-sm font-bold uppercase tracking-wide text-slate-500">{children}</h4>;

function DataTable({ columns, testId, children }) {
  return (
    <Card className="overflow-x-auto border-slate-200 shadow-sm">
      <Table data-testid={testId}>
        <TableHeader><TableRow className="bg-slate-50">{columns.map((h) => <TableHead key={h} className="text-xs font-bold uppercase tracking-wide text-slate-500">{h}</TableHead>)}</TableRow></TableHeader>
        <TableBody>{children}</TableBody>
      </Table>
    </Card>
  );
}

function Stat({ label, value, cls }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <p className={`text-2xl font-extrabold tabular-nums ${cls}`}>{value}</p>
      <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    </div>
  );
}

// The purple Letter card: approved points + one shield per letter award (sits beside the Lettering title).
function LetterCard({ ls }) {
  const awards = (ls.first_letter_earned ? 1 : 0) + (ls.additional_awards || 0);
  return (
    <div className="inline-flex items-center gap-3 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5" data-testid="member-detail-lettering-stat">
      <span className="text-lg font-extrabold tabular-nums text-violet-700">{ls.total_approved_points || 0}<span className="ml-1 text-[11px] font-semibold uppercase tracking-wide text-violet-500">pts</span></span>
      <span className="flex gap-0.5" data-testid="member-detail-medals" title={`${awards} letter award${awards === 1 ? "" : "s"} earned`}>
        {[1, 2, 3, 4].map((i) => <Shield key={i} className={`h-4 w-4 ${i <= awards ? "fill-violet-100 text-violet-600" : "text-violet-300"}`} data-testid={`member-detail-medal-${i}`} />)}
      </span>
    </div>
  );
}

function ThespianCard({ t }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5" data-testid="member-detail-thespian-stat">
      <Drama className="h-4 w-4 text-amber-600" />
      <span className="text-lg font-extrabold tabular-nums text-amber-700">{fmtPts(t.total_points)}<span className="ml-1 text-[11px] font-semibold uppercase tracking-wide text-amber-600">pts</span></span>
    </div>
  );
}

function ThespianSection({ t, hl, focusRow, focusId }) {
  const items = t.items || [];
  return (
    <div className="mt-4" data-testid="member-detail-thespian">
      <div className="mb-2 flex flex-wrap items-center gap-3"><SectionTitle>Thespian Points</SectionTitle><ThespianCard t={t} /></div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Pill className={t.rank ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-500"} testId="member-detail-thespian-rank">{t.rank || "Not yet inducted"}</Pill>
        <Pill>Approved claims: {t.approved_count || 0}</Pill>
        {t.next_rank && <Pill>{fmtPts(t.points_to_next)} pts to {t.next_rank}</Pill>}
      </div>
      {items.length === 0 ? <Empty text="No Thespian point claims." testId="member-detail-thespian-empty" /> : (
        <DataTable columns={["Date", "Activity", "Points", "Status"]} testId="member-detail-thespian-table">
          {items.map((c) => (
            <TableRow key={c.id} ref={c.id === focusId ? focusRow : undefined} className={hl(c.id)} data-testid={`member-detail-thespian-${c.id}`}>
              <TableCell className="whitespace-nowrap text-sm tabular-nums">{formatDateOnly(c.activity_date)}</TableCell>
              <TableCell className="max-w-xs"><ActivityLabel c={c} /></TableCell>
              <TableCell className={`font-bold tabular-nums ${c.status === "approved" ? "text-emerald-700" : "text-amber-700"}`}>{fmtPts(c.status === "approved" ? c.points_awarded : c.points_claimed)}</TableCell>
              <TableCell><StatusBadge status={c.status} /></TableCell>
            </TableRow>
          ))}
        </DataTable>
      )}
    </div>
  );
}

// Same card as the Merit System page, without filter badges: every case, newest first (auto load-more).
function SummaryCases({ cases, focusId, focusRow, hl }) {
  const { visible, hasMore, loadMore } = useReveal(cases, Math.max(50, cases.findIndex((c) => c.case_number === focusId) + 1));
  return (
    <SubmissionReviewCard title="Merit & Demerit Cases" subtitle={`${cases.length} case${cases.length === 1 ? "" : "s"} · newest first`} icon={Gavel}
      iconClass="bg-red-100 text-red-700 ring-1 ring-red-200" filters={[]} columns={caseColumns(false)} rowCount={visible.length}
      emptyText={CASE_EMPTY.all} hasMore={hasMore} onLoadMore={loadMore} testId="member-detail-cases" className="mb-0">
      {visible.map((c) => (
        <MeritCaseRow key={c.id} ref={c.case_number === focusId ? focusRow : undefined} className={hl(c.case_number)}
          defaultOpen={c.case_number === focusId} compact c={c} confTestId="detail-conf-when" testId={`member-detail-case-${c.case_number}`}
          actionExtra={hasConference(c) ? <ConferenceLog caseItem={c} /> : null} />
      ))}
    </SubmissionReviewCard>
  );
}

function awardedText(r) {
  if (r.status === "approved" && r.assigned_value) return `+${r.assigned_value}`;
  if (r.status === "disapproved") return "—";
  if (r.value_type === "single" && r.assigned_value) return `${r.assigned_value} pts`;
  if (r.value_type === "range") return `${r.preset_min ?? 1}–${r.preset_max ?? 10} pts`;
  return "—";
}

// View-only Member Summary: the only action is Print summary (plus the read-only conference log).
export default function MemberSummaryDialog({ detail, onClose, groups = [], showThespian, focusId }) {
  const focusRow = useCallback((el) => {
    if (el) setTimeout(() => { try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* noop */ } }, 350);
  }, []);
  const hl = (num) => (focusId && num === focusId ? "bg-amber-100 ring-2 ring-inset ring-amber-400 transition-colors" : "");
  const printSummary = async () => {
    try {
      const res = await api.get(`/coach/team-members/${detail.member.id}/summary.pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `member_summary_${detail.member.last_name}_${detail.member.first_name}.pdf`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Could not generate the summary PDF"); }
  };

  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto [&>*]:min-w-0" data-testid="coach-member-detail-dialog">
        {detail && (() => {
          const ls = detail.letter_status || {};
          const b = detail.breakdown || {};
          return (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading" data-testid="member-detail-name">{detail.member.last_name}, {detail.member.first_name}</DialogTitle>
                <p className="text-sm text-slate-500">{detail.member.email}</p>
                {groups.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1" data-testid="member-detail-groups">
                    {groups.map((g) => <span key={g} className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-medium text-violet-700">{g}</span>)}
                  </div>
                )}
              </DialogHeader>
              <div className="-mt-2 flex justify-end">
                <Button size="sm" variant="outline" className="rounded-full" onClick={printSummary} data-testid="member-detail-print-summary"><Printer className="mr-1 h-3.5 w-3.5" /> Print summary</Button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Stat label="Merits" value={b.total_merits ?? 0} cls="text-emerald-600" />
                <Stat label="Demerits" value={b.total_demerits ?? 0} cls="text-red-600" />
                <Stat label="Net" value={b.net ?? 0} cls={(b.net ?? 0) < 0 ? "text-red-600" : "text-slate-800"} />
              </div>

              <div className="mt-2">
                <SummaryCases cases={detail.cases} focusId={focusId} focusRow={focusRow} hl={hl} />
              </div>

              <div className="mt-4">
                <div className="mb-2"><SectionTitle>Merit Requests</SectionTitle></div>
                {detail.requests.length === 0 ? <Empty text="No merit requests." testId="member-detail-requests-empty" /> : (
                  <DataTable columns={["Request #", "Event", "Date", "Status", "Awarded"]} testId="member-detail-requests-table">
                    {detail.requests.map((r) => (
                      <TableRow key={r.id} ref={r.request_number === focusId ? focusRow : undefined} className={hl(r.request_number)} data-testid={`member-detail-request-${r.request_number}`}>
                        <TableCell className="whitespace-nowrap font-mono text-xs font-semibold">{shortNo(r.request_number)}</TableCell>
                        <TableCell className="text-sm font-medium text-slate-800">{r.event_name}</TableCell>
                        <TableCell className="text-sm">{fmtDate(r.event_date)}</TableCell>
                        <TableCell><StatusBadge status={r.status} /></TableCell>
                        <TableCell className="font-bold tabular-nums text-emerald-600">{awardedText(r)}</TableCell>
                      </TableRow>
                    ))}
                  </DataTable>
                )}
              </div>

              <div className="mt-4">
                <div className="mb-2 flex flex-wrap items-center gap-3"><SectionTitle>Lettering</SectionTitle><LetterCard ls={ls} /></div>
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Pill className={ls.first_letter_earned ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-500"} testId="member-detail-first-letter">
                    {ls.first_letter_earned ? "First letter earned" : `${ls.points_to_first_letter ?? 0} pts to first letter`}
                  </Pill>
                  <Pill>Additional awards: {ls.additional_awards || 0}{(ls.additional_award_years || []).length ? ` (${ls.additional_award_years.join(", ")})` : ""}</Pill>
                  <Pill>Approved points: {ls.total_approved_points || 0}</Pill>
                  <Pill>Years on team: {ls.years_on_team ?? "—"}</Pill>
                </div>
                {detail.lettering.length === 0 ? <Empty text="No lettering submissions." testId="member-detail-lettering-empty" /> : (
                  <DataTable columns={["Case #", "Event Date", "Achievement", "Points", "Status"]} testId="member-detail-lettering-table">
                    {detail.lettering.map((s) => (
                      <TableRow key={s.id} ref={s.case_number === focusId ? focusRow : undefined} className={hl(s.case_number)} data-testid={`member-detail-lettering-${s.case_number}`}>
                        <TableCell className="whitespace-nowrap font-mono text-xs font-semibold">{shortNo(s.case_number)}</TableCell>
                        <TableCell className="text-sm">{fmtDate(s.event_date)}</TableCell>
                        <TableCell className="text-sm text-slate-700">{s.achievement_label_snapshot}</TableCell>
                        <TableCell className="font-bold tabular-nums text-violet-600">{s.points_requested_snapshot}</TableCell>
                        <TableCell><StatusBadge status={s.status} /></TableCell>
                      </TableRow>
                    ))}
                  </DataTable>
                )}
              </div>

              {showThespian && detail.thespian?.enabled && <ThespianSection t={detail.thespian} hl={hl} focusRow={focusRow} focusId={focusId} />}
            </>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}
